import { MONTH_ABBR_PT, PLANNING_NUTRIENT_HEADERS } from '../constants/maintenance.constants';
import type { MaintenanceSectorFertilization } from '../types';
import type { SimpleDataTableColumn, SimpleDataTableRow } from '../components/Tables/SimpleDataTable/SimpleDataTable';

export interface PlanningTableData {
	columns: SimpleDataTableColumn[];
	rows: SimpleDataTableRow[];
}

type ProposalItem = { id: string; dose: number; name?: string };
type FertilizerItem = { id: string; dose: number; name?: string };

type SectorRecommendation = {
	period?: string;
	proposal?: ProposalItem[];
	balance?: number[];
	fertilizers?: FertilizerItem[];
	irrigation?: {
		vbox?: number;
		cemMax?: number;
		injectorFlow?: number;
		saltFraction?: number;
		desiredEvents?: number | null;
	};
};

export type PlanningSectorResult = {
	sectorId: string;
	sectorName: string;
	size: number;
	recommendations?: SectorRecommendation[] | SectorRecommendation;
	recommendation?: {
		fertilizers?: FertilizerItem[];
		demand?: Record<string, number>;
	};
};

const doseFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 1,
	maximumFractionDigits: 1,
});

const percentFormatter = new Intl.NumberFormat('pt-BR', {
	maximumFractionDigits: 0,
});

const formatDose = (value: number | null | undefined) => {
	if (value == null || !Number.isFinite(value) || value === 0) return '—';
	return doseFormatter.format(value);
};

const formatPercent = (value: number | null | undefined) => {
	if (value == null || !Number.isFinite(value) || value === 0) return '—';
	return percentFormatter.format(value);
};

export const toApiPeriod = (value: string) => value.replace(/-/g, '/').slice(0, 7);

export const fromApiPeriod = (value: string) => value.replace(/\//g, '-').slice(0, 7);

export const formatSaltFractionPercent = (fraction?: number) => {
	if (fraction == null || !Number.isFinite(fraction)) return '';
	const percent = fraction * 100;
	return Number.isInteger(percent) ? String(percent) : String(Math.round(percent * 10) / 10);
};

export const toApiSaltFraction = (percent: string) => Number(percent) / 100;

export const formatPlanningMonth = (period: string) => {
	const normalized = fromApiPeriod(period);
	const [year, month] = normalized.split('-');
	const abbr = MONTH_ABBR_PT[Number(month) - 1];
	return abbr && year ? `${abbr}/${year}` : period;
};

const asRecommendationList = (sector: PlanningSectorResult): SectorRecommendation[] => {
	if (Array.isArray(sector.recommendations)) return sector.recommendations;
	if (sector.recommendations) return [sector.recommendations];
	if (sector.recommendation) return [sector.recommendation];
	return [];
};

const fertilizerName = (
	id: string,
	fallback?: string,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
) => {
	const product = products?.find((item) => item._id === id);
	return fallback || product?.name || product?.commercialName || 'Produto';
};

const collectFertilizers = (
	sectors: PlanningSectorResult[],
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
) => {
	const map = new Map<string, string>();
	sectors.forEach((sector) => {
		asRecommendationList(sector).forEach((recommendation) => {
			(recommendation.proposal ?? recommendation.fertilizers ?? []).forEach((item) => {
				const id = String(item.id ?? '');
				if (!id || map.has(id)) return;
				map.set(id, fertilizerName(id, item.name, products));
			});
		});
	});
	return [...map.entries()].map(([id, name]) => ({ id, name }));
};

const periodRows = (sectors: PlanningSectorResult[], byHectare: boolean) => {
	const rows = new Map<string, Record<string, number>>();
	const source = byHectare ? sectors.slice(0, 1) : sectors;
	source.forEach((sector) => {
		const size = byHectare ? 1 : Number(sector.size) || 1;
		asRecommendationList(sector).forEach((recommendation) => {
			const period = recommendation.period || 'total';
			if (!rows.has(period)) rows.set(period, {});
			const cells = rows.get(period);
			if (!cells) return;
			(recommendation.proposal ?? recommendation.fertilizers ?? []).forEach((item) => {
				const id = String(item.id ?? '');
				if (!id) return;
				cells[id] = (cells[id] ?? 0) + Number(item.dose || 0) * size;
			});
		});
	});
	return [...rows.entries()].sort(([a], [b]) => a.localeCompare(b));
};

export const mapPlanningFertilizerDemand = (
	sectors: PlanningSectorResult[] = [],
	byHectare: boolean,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
): PlanningTableData => {
	const fertilizers = collectFertilizers(sectors, products);
	const columns: SimpleDataTableColumn[] = [
		{ key: 'month', label: 'MÊS' },
		...fertilizers.map((fertilizer) => ({
			key: fertilizer.id,
			label: fertilizer.name.toUpperCase(),
		})),
	];
	const periodEntries = periodRows(sectors, byHectare);
	const totals: Record<string, number> = {};
	const rows: SimpleDataTableRow[] = periodEntries.map(([period, cells]) => {
		fertilizers.forEach(({ id }) => {
			totals[id] = (totals[id] ?? 0) + (cells[id] ?? 0);
		});
		return {
			id: period,
			cells: {
				month: formatPlanningMonth(period),
				...Object.fromEntries(fertilizers.map(({ id }) => [id, formatDose(cells[id])])),
			},
		};
	});

	if (rows.length > 0) {
		rows.push({
			id: 'period-total',
			isTotal: true,
			cells: {
				month: 'Total do período',
				...Object.fromEntries(fertilizers.map(({ id }) => [id, formatDose(totals[id])])),
			},
		});
	}

	return { columns, rows };
};

export const mapPlanningNutritionBalance = (
	sectors: PlanningSectorResult[] = []
): PlanningTableData => {
	const columns: SimpleDataTableColumn[] = [
		{ key: 'month', label: 'MÊS' },
		...PLANNING_NUTRIENT_HEADERS.map((nutrient) => ({
			key: nutrient.key,
			label: nutrient.label,
		})),
	];
	const byPeriod = new Map<string, number[]>();
	sectors.forEach((sector) => {
		asRecommendationList(sector).forEach((recommendation) => {
			if (!recommendation.balance?.length) return;
			const period = recommendation.period || 'total';
			byPeriod.set(period, recommendation.balance);
		});
	});

	const rows: SimpleDataTableRow[] = [...byPeriod.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([period, balance]) => ({
			id: period,
			cells: {
				month: formatPlanningMonth(period),
				...Object.fromEntries(
					PLANNING_NUTRIENT_HEADERS.map((nutrient, index) => [
						nutrient.key,
						formatPercent(balance[index]),
					])
				),
			},
		}));

	return { columns, rows };
};

export const mapSectorFertilizationsToPlanning = (
	items: MaintenanceSectorFertilization[] = []
): PlanningSectorResult[] =>
	items.map((item) => ({
		sectorId: item.sectorId,
		sectorName: item.sectorName,
		size: item.size,
		recommendations: Array.isArray(item.recommendations)
			? item.recommendations
			: item.recommendations
				? [item.recommendations]
				: [],
	}));
